import React, { useState } from 'react';
import { BrainIcon, RefreshCwIcon, AlertTriangleIcon, CalendarIcon, ChevronRightIcon, FileTextIcon, ServerIcon, WrenchIcon } from 'lucide-react';
interface AIInsightPanelProps {
  activeFilter: string;
  activeCategory: string;
  activeStatus: string;
  activeClassification: string;
  dateRange: {
    start: string;
    end: string;
  };
}
export const AIInsightPanel: React.FC<AIInsightPanelProps> = ({
  activeFilter,
  activeCategory,
  activeStatus,
  activeClassification,
  dateRange
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('critical'); // 'critical', 'expiring', 'recommendations'
  // Most critical assets
  const criticalAssets = [{
    id: 'AST004',
    name: 'Firewall Pusat Data',
    location: 'BINDA Sumbar',
    status: 'critical',
    score: 92,
    issue: 'Firmware kadaluarsa, rentan terhadap eksploitasi keamanan'
  }, {
    id: 'AST012',
    name: 'Server Utama BINDA Jatim',
    location: 'BINDA Jatim',
    status: 'critical',
    score: 87,
    issue: 'Overheating, perlu peningkatan sistem pendingin'
  }, {
    id: 'AST023',
    name: 'Perangkat Enkripsi Jaringan',
    location: 'BINDA Jatim',
    status: 'critical',
    score: 85,
    issue: 'Sertifikat keamanan akan habis, perlu pembaruan segera'
  }, {
    id: 'AST002',
    name: 'Router Backbone STIN',
    location: 'STIN',
    status: 'critical',
    score: 83,
    issue: 'Degradasi performa, butuh pemeliharaan preventif'
  }, {
    id: 'AST042',
    name: 'Sistem Backup Pusat',
    location: 'Pusat/Setuma',
    status: 'critical',
    score: 80,
    issue: 'Kapasitas penyimpanan hampir penuh (92%), perlu perluasan'
  }];
  // Expiring contracts/licenses
  const expiringAssets = [{
    id: 'AST010',
    name: 'Lisensi Analitik Big Data',
    location: 'Pusat/Setuma',
    expiryDate: '2026-03-05',
    daysLeft: 15,
    value: 'Rp 220M'
  }, {
    id: 'AST003',
    name: 'Lisensi Oracle BINDA Jatim',
    location: 'BINDA Jatim',
    expiryDate: '2026-03-10',
    daysLeft: 20,
    value: 'Rp 85M'
  }, {
    id: 'AST056',
    name: 'Sewa Gedung BINDA Sulsel',
    location: 'BINDA Sulsel',
    expiryDate: '2026-03-20',
    daysLeft: 30,
    value: 'Rp 120M'
  }, {
    id: 'AST078',
    name: 'Lisensi Antivirus Enterprise',
    location: 'Pusat/Setuma',
    expiryDate: '2026-04-06',
    daysLeft: 47,
    value: 'Rp 75M'
  }, {
    id: 'AST004',
    name: 'Firewall Pusat Data',
    location: 'BINDA Sumbar',
    expiryDate: '2026-04-15',
    daysLeft: 56,
    value: 'Rp 75M'
  }, {
    id: 'AST092',
    name: 'Sewa Gudang Logistik Deputi II',
    location: 'Deputi II',
    expiryDate: '2026-05-06',
    daysLeft: 77,
    value: 'Rp 45M'
  }, {
    id: 'AST001',
    name: 'Server Database Utama',
    location: 'Pusat/Setuma',
    expiryDate: '2026-06-04',
    daysLeft: 106,
    value: 'Rp 450M'
  }];
  // AI Recommendations
  const recommendations = [{
    id: 'REC001',
    title: 'Buat RKA untuk Server Utama',
    description: 'Server Database Utama perlu upgrade dalam 6 bulan ke depan. Buat RKA sekarang untuk anggaran 2027.',
    priority: 'high',
    icon: <FileTextIcon size={18} className="text-white" data-id="element-186" />,
    iconBg: 'bg-accent-info'
  }, {
    id: 'REC002',
    title: 'Jadwalkan Servis Router Backbone STIN',
    description: 'Jadwalkan pemeliharaan preventif untuk mencegah degradasi performa lebih lanjut.',
    priority: 'high',
    icon: <WrenchIcon size={18} className="text-white" data-id="element-187" />,
    iconBg: 'bg-yellow-500'
  }, {
    id: 'REC003',
    title: 'Konsolidasi Lisensi Software',
    description: 'Gabungkan pembelian lisensi Oracle untuk mendapatkan diskon volume hingga 15%.',
    priority: 'medium',
    icon: <FileTextIcon size={18} className="text-white" data-id="element-188" />,
    iconBg: 'bg-green-500'
  }, {
    id: 'REC004',
    title: 'Upgrade Sistem Pendingin',
    description: 'Tingkatkan sistem pendingin di pusat data BINDA Jatim untuk mengatasi masalah overheating.',
    priority: 'high',
    icon: <ServerIcon size={18} className="text-white" data-id="element-189" />,
    iconBg: 'bg-accent-red'
  }];
  // Filter based on active filters
  const filterInsights = insights => {
    // This would normally filter based on activeFilter, activeCategory, etc.
    // For demo purposes, we'll just return the insights
    return insights;
  };
  const filteredCriticalAssets = filterInsights(criticalAssets);
  const filteredExpiringAssets = filterInsights(expiringAssets);
  const filteredRecommendations = filterInsights(recommendations);
  const handleRefreshInsights = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1500);
  };
  return <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-190">
      <div className="p-3 border-b border-gray-200 bg-neutral-light flex justify-between items-center" data-id="element-191">
        <div className="flex items-center" data-id="element-192">
          <BrainIcon size={18} className="text-accent-info mr-2" data-id="element-193" />
          <h2 className="font-bold text-secondary" data-id="element-194">AI Insights</h2>
        </div>
        <button className="p-1 hover:bg-gray-200 rounded-full transition-colors" onClick={handleRefreshInsights} disabled={isRefreshing} data-id="element-195">
          <RefreshCwIcon size={16} className={`text-secondary ${isRefreshing ? 'animate-spin' : ''}`} data-id="element-196" />
        </button>
      </div>
      {/* Tabs */}
      <div className="flex border-b border-gray-200" data-id="element-197">
        <button className={`flex-1 py-2 text-sm font-medium ${activeTab === 'critical' ? 'text-accent-red border-b-2 border-accent-red' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('critical')} data-id="element-198">
          5 Aset Paling Kritis
        </button>
        <button className={`flex-1 py-2 text-sm font-medium ${activeTab === 'expiring' ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('expiring')} data-id="element-199">
          Timeline Expired
        </button>
        <button className={`flex-1 py-2 text-sm font-medium ${activeTab === 'recommendations' ? 'text-accent-info border-b-2 border-accent-info' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('recommendations')} data-id="element-200">
          Rekomendasi
        </button>
      </div>
      <div className="p-4" data-id="element-201">
        {/* Critical Assets Tab */}
        {activeTab === 'critical' && <div className="space-y-3" data-id="element-202">
            {filteredCriticalAssets.map((asset, index) => <div key={asset.id} className="bg-neutral-light p-3 rounded-lg" data-id="element-203">
                <div className="flex items-start" data-id="element-204">
                  <div className="flex-shrink-0 w-8 h-8 bg-accent-red rounded-full flex items-center justify-center mr-3" data-id="element-205">
                    <span className="text-white text-xs font-bold" data-id="element-206">
                      {index + 1}
                    </span>
                  </div>
                  <div className="flex-grow" data-id="element-207">
                    <h3 className="text-sm font-medium text-secondary flex items-center" data-id="element-208">
                      {asset.name}
                      <span className="ml-2 text-xs bg-accent-red text-white px-1.5 py-0.5 rounded" data-id="element-209">
                        {asset.score}/100
                      </span>
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-210">
                      {asset.location}
                    </p>
                    <p className="text-sm text-gray-700 mt-1" data-id="element-211">
                      <span className="font-medium" data-id="element-212">Issue:</span> {asset.issue}
                    </p>
                    <div className="mt-2 flex justify-end" data-id="element-213">
                      <button className="text-xs text-accent-red hover:underline flex items-center" data-id="element-214">
                        Lihat Detail{' '}
                        <ChevronRightIcon size={14} className="ml-0.5" data-id="element-215" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>)}
          </div>}
        {/* Expiring Assets Tab */}
        {activeTab === 'expiring' && <div className="space-y-4" data-id="element-216">
            <div className="relative pt-4" data-id="element-217">
              {/* Timeline line */}
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" data-id="element-218"></div>
              {/* Timeline entries */}
              <div className="space-y-4" data-id="element-219">
                <div className="ml-2 text-xs font-medium text-gray-500" data-id="element-220">
                  30 HARI KE DEPAN
                </div>
                {filteredExpiringAssets.filter(asset => asset.daysLeft <= 30).map(asset => <div key={asset.id} className="relative pl-10" data-id="element-221">
                      <div className="absolute left-3 top-1.5 w-3 h-3 rounded-full bg-accent-red border-2 border-white" data-id="element-222"></div>
                      <div className="bg-red-50 p-3 rounded-lg border border-red-100" data-id="element-223">
                        <div className="flex justify-between items-start" data-id="element-224">
                          <div data-id="element-225">
                            <h3 className="text-sm font-medium text-secondary" data-id="element-226">
                              {asset.name}
                            </h3>
                            <p className="text-xs text-gray-500" data-id="element-227">
                              {asset.location}
                            </p>
                          </div>
                          <span className="text-xs font-medium bg-red-100 text-red-800 px-2 py-0.5 rounded-full" data-id="element-228">
                            {asset.daysLeft} hari
                          </span>
                        </div>
                        <div className="mt-1 flex justify-between items-center" data-id="element-229">
                          <div className="flex items-center text-xs text-gray-500" data-id="element-230">
                            <CalendarIcon size={12} className="mr-1" data-id="element-231" />
                            {asset.expiryDate}
                          </div>
                          <div className="text-xs font-medium" data-id="element-232">
                            {asset.value}
                          </div>
                        </div>
                        <div className="mt-2 flex justify-end" data-id="element-233">
                          <button className="text-xs text-accent-red hover:underline" data-id="element-234">
                            Ajukan Perpanjangan
                          </button>
                        </div>
                      </div>
                    </div>)}
                <div className="ml-2 text-xs font-medium text-gray-500" data-id="element-235">
                  31-90 HARI
                </div>
                {filteredExpiringAssets.filter(asset => asset.daysLeft > 30 && asset.daysLeft <= 90).map(asset => <div key={asset.id} className="relative pl-10" data-id="element-236">
                      <div className="absolute left-3 top-1.5 w-3 h-3 rounded-full bg-orange-500 border-2 border-white" data-id="element-237"></div>
                      <div className="bg-orange-50 p-3 rounded-lg border border-orange-100" data-id="element-238">
                        <div className="flex justify-between items-start" data-id="element-239">
                          <div data-id="element-240">
                            <h3 className="text-sm font-medium text-secondary" data-id="element-241">
                              {asset.name}
                            </h3>
                            <p className="text-xs text-gray-500" data-id="element-242">
                              {asset.location}
                            </p>
                          </div>
                          <span className="text-xs font-medium bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full" data-id="element-243">
                            {asset.daysLeft} hari
                          </span>
                        </div>
                        <div className="mt-1 flex justify-between items-center" data-id="element-244">
                          <div className="flex items-center text-xs text-gray-500" data-id="element-245">
                            <CalendarIcon size={12} className="mr-1" data-id="element-246" />
                            {asset.expiryDate}
                          </div>
                          <div className="text-xs font-medium" data-id="element-247">
                            {asset.value}
                          </div>
                        </div>
                        <div className="mt-2 flex justify-end" data-id="element-248">
                          <button className="text-xs text-orange-600 hover:underline" data-id="element-249">
                            Buat Pengingat
                          </button>
                        </div>
                      </div>
                    </div>)}
                <div className="ml-2 text-xs font-medium text-gray-500" data-id="element-250">
                  91-180 HARI
                </div>
                {filteredExpiringAssets.filter(asset => asset.daysLeft > 90 && asset.daysLeft <= 180).map(asset => <div key={asset.id} className="relative pl-10" data-id="element-251">
                      <div className="absolute left-3 top-1.5 w-3 h-3 rounded-full bg-yellow-500 border-2 border-white" data-id="element-252"></div>
                      <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-100" data-id="element-253">
                        <div className="flex justify-between items-start" data-id="element-254">
                          <div data-id="element-255">
                            <h3 className="text-sm font-medium text-secondary" data-id="element-256">
                              {asset.name}
                            </h3>
                            <p className="text-xs text-gray-500" data-id="element-257">
                              {asset.location}
                            </p>
                          </div>
                          <span className="text-xs font-medium bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full" data-id="element-258">
                            {asset.daysLeft} hari
                          </span>
                        </div>
                        <div className="mt-1 flex justify-between items-center" data-id="element-259">
                          <div className="flex items-center text-xs text-gray-500" data-id="element-260">
                            <CalendarIcon size={12} className="mr-1" data-id="element-261" />
                            {asset.expiryDate}
                          </div>
                          <div className="text-xs font-medium" data-id="element-262">
                            {asset.value}
                          </div>
                        </div>
                        <div className="mt-2 flex justify-end" data-id="element-263">
                          <button className="text-xs text-yellow-700 hover:underline" data-id="element-264">
                            Rencanakan Anggaran
                          </button>
                        </div>
                      </div>
                    </div>)}
              </div>
            </div>
          </div>}
        {/* Recommendations Tab */}
        {activeTab === 'recommendations' && <div className="space-y-3" data-id="element-265">
            {filteredRecommendations.map(rec => <div key={rec.id} className="bg-neutral-light p-3 rounded-lg" data-id="element-266">
                <div className="flex items-start" data-id="element-267">
                  <div className={`flex-shrink-0 w-8 h-8 ${rec.iconBg} rounded-full flex items-center justify-center mr-3`} data-id="element-268">
                    {rec.icon}
                  </div>
                  <div className="flex-grow" data-id="element-269">
                    <h3 className="text-sm font-medium text-secondary flex items-center" data-id="element-270">
                      {rec.title}
                      <span className={`ml-2 text-xs ${rec.priority === 'high' ? 'bg-accent-red text-white' : rec.priority === 'medium' ? 'bg-yellow-500 text-white' : 'bg-green-500 text-white'} px-1.5 py-0.5 rounded`} data-id="element-271">
                        {rec.priority === 'high' ? 'Prioritas Tinggi' : rec.priority === 'medium' ? 'Prioritas Sedang' : 'Prioritas Rendah'}
                      </span>
                    </h3>
                    <p className="text-sm text-gray-700 mt-1" data-id="element-272">
                      {rec.description}
                    </p>
                    <div className="mt-2 flex justify-end" data-id="element-273">
                      {rec.priority === 'high' ? <button className="text-xs bg-accent-info text-white px-2 py-1 rounded hover:bg-opacity-90" data-id="element-274">
                          Ambil Tindakan
                        </button> : <button className="text-xs text-accent-info hover:underline" data-id="element-275">
                          Lihat Detail
                        </button>}
                    </div>
                  </div>
                </div>
              </div>)}
          </div>}
      </div>
    </div>;
};